// The §2.21 / §4.9 SALES ANALYTICS data layer (P3-E PR-E3, D-293).
//
// ONE read, `GET /v1/sales-stats`, riding `sales.read`. The server decides the
// narrowing and the §7 money redaction; this layer neither re-asks nor
// re-derives either — `values_visible` on the response is what the screen
// branches on, never "is the first row's money null", which would read an
// empty page as a permission answer.
//
// THE RANGE IS ALWAYS EXPLICIT. Both `from` and `to` are required by the
// contract, so the screen computes them from the TENANT's today (never the
// browser's — D-203's lesson, where §4.4's wanted_date was the browser's day
// while the save priced at the tenant's) and passes them every time.

import { useQuery } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type SalesStatsRow = S["SalesStatsRow"];
export type SalesStatsResponse = S["SalesStatsResponse"];

export interface SalesStatsRange {
  from: string;
  to: string;
  ownerUserId?: string | undefined;
  tier?: SalesStatsRow["tier"] | undefined;
}

export function salesStatsKey(range: SalesStatsRange) {
  return [
    "sales",
    "stats",
    range.from,
    range.to,
    range.ownerUserId ?? null,
    range.tier ?? null,
  ] as const;
}

export function useSalesStats(range: SalesStatsRange, enabled: boolean) {
  return useQuery({
    queryKey: salesStatsKey(range),
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/sales-stats", {
          params: {
            query: {
              from: range.from,
              to: range.to,
              ...(range.ownerUserId !== undefined ? { owner_user_id: range.ownerUserId } : {}),
              ...(range.tier !== undefined ? { tier: range.tier } : {}),
            },
          },
          signal,
        }),
        "Could not load the analytics",
      ),
  });
}

export type SalesOffListLine = S["SalesOffListLine"];

/**
 * §10.13's DRILL-THROUGH — the off-list lines behind one manager's rate.
 *
 * `enabled` is what the screen opens: nothing is fetched until a reader asks
 * for a specific manager's lines, because this read names customers' documents
 * and their prices and there is no reason to pull it to render a percentage.
 */
export function useOffListLines(
  range: SalesStatsRange,
  enabled: boolean,
): ReturnType<
  typeof useQuery<{ items: SalesOffListLine[]; values_visible: boolean; truncated: boolean }>
> {
  return useQuery({
    queryKey: ["sales", "stats", "off-list", ...salesStatsKey(range).slice(2)],
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/sales-stats/off-list-lines", {
          params: {
            query: {
              from: range.from,
              to: range.to,
              ...(range.ownerUserId !== undefined ? { owner_user_id: range.ownerUserId } : {}),
              ...(range.tier !== undefined ? { tier: range.tier } : {}),
            },
          },
          signal,
        }),
        "Could not load the off-list lines",
      ),
  });
}

/**
 * What the manager typed MINUS what the engine resolved, in minor units.
 *
 * NULL when either side is missing — a redacted price (§7, absent from the
 * payload since D-337 ruling 1) or a line where no list rate resolved at all
 * (null). A difference against a missing number is not a difference, and
 * printing one would invent a discount nobody gave.
 */
export function priceDelta(line: {
  list_price_minor?: number | null;
  unit_price_minor?: number | null;
}): number | null {
  const listed = line.list_price_minor;
  const typed = line.unit_price_minor;
  if (listed === null || listed === undefined || typed === null || typed === undefined) {
    return null;
  }
  return typed - listed;
}

// ---------------------------------------------------------------------------
// PURE SHAPING — exported so the unit test asserts the arithmetic directly
// rather than through a rendered table (the wall-board lesson: a control count
// over a leaf component is not a claim about the numbers).
// ---------------------------------------------------------------------------

export interface ManagerTotals {
  ownerUserId: string;
  /** §2.21's grain and §4.9's own element ("win-rate by tier"): the tier is
   * part of the KEY, not folded away. A manager who sells on-list to b2b and
   * discounts every distributor line has two different habits, and one blended
   * row would hide the one worth acting on. */
  tier: SalesStatsRow["tier"];
  currency: string;
  quotesSent: number;
  quotesAccepted: number;
  offListLines: number;
  marginBreachLines: number;
  ordersConfirmed: number;
  /** NULL when the caller may not see money — never 0, which would read as
   * "sold nothing" (§7's redaction is not a value). */
  orderValueMinor: number | null;
  offcutValueMinor: number | null;
  /** Lines that carried a price at all, the denominator of the off-list rate. */
  pricedLines: number;
}

/**
 * Fold the daily rows into one row per (manager, TIER, currency) — §2.21's own
 * grain minus the day.
 *
 * CURRENCY STAYS IN THE KEY (D-293 ruling 4, §9.13). A manager trading in KZT
 * and USD gets two rows here, and no addition ever crosses them — this is the
 * one place a client could quietly sum mixed minor units, so it is the one
 * place that says out loud that it does not.
 *
 * TIER STAYS IN THE KEY TOO, and that is §4.9's "win-rate by tier" element
 * rather than a second chart: the rate a manager gets judged on is per tier,
 * because the discipline being measured IS tier discipline. The first cut
 * folded tiers together, which made the screen's own header comment untrue
 * and quietly dropped one of §4.9's listed elements.
 */
export function totalsByManager(rows: readonly SalesStatsRow[]): ManagerTotals[] {
  const byKey = new Map<string, ManagerTotals>();
  for (const r of rows) {
    const key = `${r.owner_user_id}|${r.tier}|${r.currency}`;
    const t = byKey.get(key) ?? {
      ownerUserId: r.owner_user_id,
      tier: r.tier,
      currency: r.currency,
      quotesSent: 0,
      quotesAccepted: 0,
      offListLines: 0,
      marginBreachLines: 0,
      ordersConfirmed: 0,
      orderValueMinor: r.order_value_minor === null ? null : 0,
      offcutValueMinor: r.offcut_value_minor === null ? null : 0,
      pricedLines: 0,
    };
    t.quotesSent += r.quotes_sent;
    t.quotesAccepted += r.quotes_accepted;
    t.offListLines += r.off_list_lines;
    t.marginBreachLines += r.margin_breach_lines;
    t.pricedLines += r.priced_lines;
    t.ordersConfirmed += r.orders_confirmed;
    if (t.orderValueMinor !== null && r.order_value_minor !== null) {
      t.orderValueMinor += r.order_value_minor;
    }
    if (t.offcutValueMinor !== null && r.offcut_value_minor !== null) {
      t.offcutValueMinor += r.offcut_value_minor;
    }
    byKey.set(key, t);
  }
  return [...byKey.values()].sort(
    (a, b) =>
      a.ownerUserId.localeCompare(b.ownerUserId) ||
      a.tier.localeCompare(b.tier) ||
      a.currency.localeCompare(b.currency),
  );
}

/**
 * accepted / sent over the window (§3.10: computed at query time, never a
 * stored column, so a different window needs no different rollup).
 *
 * NULL when nothing was sent — NOT 0%. A manager who sent no quotes has no win
 * rate, and printing 0% would read as "sent plenty, won none", which is the
 * opposite of the truth and the kind of number a manager gets judged on.
 */
export function winRate(sent: number, accepted: number): number | null {
  if (sent <= 0) return null;
  return accepted / sent;
}

/**
 * off-list lines / lines that carried a price (§4.9's tier discipline).
 *
 * NULL on a zero denominator, for the reason `winRate` states. The denominator
 * is the count of lines the rollup saw priced, which is why `pricedLines` is
 * folded above rather than reconstructed from the off-list count.
 */
export function offListRate(pricedLines: number, offList: number): number | null {
  if (pricedLines <= 0) return null;
  return offList / pricedLines;
}

/** The daily off-list rate per day, oldest first — the §4.9 trend line's
 * input. A day with no priced line contributes a GAP, not a zero: the chart
 * must not draw a manager down to 0% on a day they simply did not quote. */
export function offListTrend(
  rows: readonly SalesStatsRow[],
): { statDate: string; rate: number | null }[] {
  const byDay = new Map<string, { priced: number; offList: number }>();
  for (const r of rows) {
    const d = byDay.get(r.stat_date) ?? { priced: 0, offList: 0 };
    d.priced += r.priced_lines;
    d.offList += r.off_list_lines;
    byDay.set(r.stat_date, d);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([statDate, d]) => ({ statDate, rate: offListRate(d.priced, d.offList) }));
}
